// Auth resilience for the CRM tRPC client — survive a Clerk session-refresh window.
//
// During a Clerk session-refresh window `getToken()` can momentarily resolve
// `null` (or a just-expired token), so a request would otherwise go out without a
// valid token and the server's `protectedProcedure` answers 401
// ("You are not authorized to access this resource. Please log in."). These pure
// helpers let the client (see `createTRPCClient` in ./trpc.ts) force a fresh token
// and replay once, while still surfacing a genuine signed-out 401.

export type GetTokenOptions = { skipCache?: boolean; template?: string };
export type GetToken = (options?: GetTokenOptions) => Promise<string | null>;

export const NULL_TOKEN_RETRIES = 3;

const defaultDelay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolve a token for an outgoing request.
 * - If `forceRefresh` is set (a retryLink replay), bypass Clerk's cache up front.
 * - If the (cached) token is null, force a fresh one (`skipCache`) with a short
 *   bounded retry — this covers the session-refresh window where the cached token
 *   is briefly unavailable.
 * - A genuinely signed-out user still resolves to `null` (→ legitimate 401), and
 *   the loop is bounded by `NULL_TOKEN_RETRIES` so it can never spin.
 */
export async function resolveAuthToken(
    getToken: GetToken,
    forceRefresh: boolean,
    delay: (ms: number) => Promise<void> = defaultDelay,
): Promise<string | null> {
    let token = await getToken(forceRefresh ? { skipCache: true } : undefined);
    for (let attempt = 0; attempt < NULL_TOKEN_RETRIES && !token; attempt++) {
        await delay(150 * (attempt + 1));
        token = await getToken({ skipCache: true });
    }
    return token;
}

export function authHeaders(token: string | null): Record<string, string> {
    return token ? { authorization: `Bearer ${token}` } : {};
}

/** Pull the HTTP status off a TRPCClientError-shaped error (`error.data.httpStatus`). */
export function getHttpStatus(error: unknown): number | undefined {
    const data = (error as { data?: { httpStatus?: unknown } } | null | undefined)?.data;
    return typeof data?.httpStatus === 'number' ? data.httpStatus : undefined;
}

/** retryLink predicate: replay an operation once when it fails with a 401. */
export function shouldRetryUnauthorized(error: unknown, attempts: number): boolean {
    if (attempts >= 2) return false;
    return getHttpStatus(error) === 401;
}
